<template>
  <DeliveryPromiseCard
    :express="dates.express"
    :free="dates.free"
    :is-express-before-christmas="isExpressDeliveryBeforeChristmas"
    :is-standard-before-christmas="isStandardDeliveryBeforeChristmas"
    :with-extra-info="shouldDisplayExtraInfo"
  >
    <template v-if="slots.title" #title>
      <slot name="title" />
    </template>

    <template #extra-info>
      <ClientOnly>
        <div class="flex items-center">
          <span class="caption">{{ i18n(translations.counterLabel) }}</span>
          <time class="caption-bold ml-4">{{ formattedTime }}</time>
        </div>
      </ClientOnly>
    </template>
  </DeliveryPromiseCard>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, useSlots } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'

import { useDeliveryBeforeChristmas } from '~/scopes/shipping/composables/useDeliveryBeforeChristmas/useDeliveryBeforeChristmas'
import { useDeliveryDatesFromShipping } from '~/scopes/shipping/composables/useDeliveryDatesFromShipping/useDeliveryDatesFromShipping'
import {
  getFormattedTime,
  getNext1PMInSellerTimezone,
  getTimezone,
} from '~/scopes/shipping/utils/deliveryDate.utils'

import DeliveryPromiseCard from '../DeliveryPromiseCard/DeliveryPromiseCard.vue'

import translations from './DeliveryPromise.translations'

const slots = useSlots()

const props = defineProps<{
  shipping: NonNullable<GetPickersResponse['selectedOffer']>['shipping']
  merchant: NonNullable<GetPickersResponse['selectedOffer']>['merchant']
}>()

const experiments = useExperiments()
const i18n = useI18n()
const currentLocale = useI18nLocale()

const dates = useDeliveryDatesFromShipping(props.shipping)
const { isStandardDeliveryBeforeChristmas, isExpressDeliveryBeforeChristmas } =
  useDeliveryBeforeChristmas(props.shipping)

const intervalId = ref()
const targetTime = ref<number | null>(null)

const timezone = computed(() => {
  return getTimezone(props.merchant)
})

const shouldDisplayExtraInfo = computed(() => {
  return (
    experiments['experiment.ppOrderBefore'] === 'withOrderBefore' &&
    targetTime.value !== null
  )
})

const formattedTime = computed(() => {
  return getFormattedTime(targetTime.value, currentLocale)
})

onMounted(() => {
  const next1PM = getNext1PMInSellerTimezone(timezone.value)
  targetTime.value = next1PM

  if (next1PM) {
    // Targetting 10s to stay align with the same line but avoid too much refresh.
    intervalId.value = setInterval(() => {
      if (targetTime.value && targetTime.value > 0) {
        targetTime.value -= 10000
      } else {
        targetTime.value = getNext1PMInSellerTimezone(timezone.value)
      }
    }, 10000)
  }
})

onUnmounted(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value)
  }
})
</script>
